/**
 * Service for managing additional product information (backoffice + customer).
 * Handles CRUD for descriptions, image uploads, and product detail fetching.
 */

import { apiClient, getAuthHeaders } from './api';
import { API_BASE_URL } from '../config/constants';

const getBackofficeHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('backoffice_access_token')}`,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProductImage {
  id: string;
  image_type: 'main' | 'color' | 'additional';
  file_url: string;
  file_path: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  alt_text: string | null;
  color_name: string | null;
  color_hex: string | null;
  sort_order: number;
}

export interface ProductAdditionalInfo {
  id: string;
  product_id: string;
  epicor_part_number: string;
  description_id: string | null;
  description_en: string | null;
  finish_type: string | null;
  main_images: ProductImage[];
  color_images: ProductImage[];
  additional_images: ProductImage[];
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductInfoListItem {
  id: string;
  epicor_part_number: string;
  name: string;
  brand_code?: string | null;
  brand_name?: string | null;
  category_item_code?: string | null;
  category_item_name?: string | null;
  family_code?: string | null;
  family_name: string | null;
  subfamily_code?: string | null;
  subfamily_name: string | null;
  is_active: boolean;
  has_additional_info: boolean;
  image_count: number;
  has_description_id: boolean;
  has_description_en: boolean;
  finish_type: string | null;
}

export interface ProductDetail {
  id: string;
  epicor_part_number: string;
  name: string;
  description: string | null;
  category: string | null;
  subcategory: string | null;
  family_code: string | null;
  family_name: string | null;
  subfamily_code: string | null;
  subfamily_name: string | null;
  unit_of_measure: string | null;
  is_active: boolean;
  image_url: string | null;
  msds_url: string | null;
  pricing: {
    unit_price: number;
    discount_percent: number;
    discounted_price: number;
    promotion_discount?: number;
  } | Record<string, never>;
  additional_info: ProductAdditionalInfo | null;
}

export interface CsvValidationError {
  row: number;
  epicor_part_number: string;
  errors: string[];
}

export interface CsvValidationResponse {
  success: boolean;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  errors: CsvValidationError[];
  error?: string;
}

export interface CsvImportRowResult {
  row_number: number;
  epicor_part_number: string;
  status: 'success' | 'partial' | 'error' | 'skipped';
  action: string;
  images_downloaded: number;
  images_failed: number;
  errors: string[];
  warnings: string[];
}

export interface CsvImportResponse {
  success: boolean;
  message: string;
  result: {
    total_rows: number;
    successful: number;
    partial: number;
    failed: number;
    skipped: number;
    rows: CsvImportRowResult[];
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert relative upload paths to absolute URLs. */
function resolveImageUrls(info: ProductAdditionalInfo | null): ProductAdditionalInfo | null {
  if (!info) return null;
  const baseUrl = API_BASE_URL.replace('/api/v1', '');

  const fixUrl = (img: ProductImage): ProductImage => ({
    ...img,
    file_url: img.file_url.startsWith('http')
      ? img.file_url
      : `${baseUrl}${img.file_url}`,
  });

  return {
    ...info,
    main_images: info.main_images.map(fixUrl),
    color_images: info.color_images.map(fixUrl),
    additional_images: info.additional_images.map(fixUrl),
  };
}

// ---------------------------------------------------------------------------
// Backoffice endpoints
// ---------------------------------------------------------------------------

class ProductInfoService {
  /** List products with additional info status (backoffice). */
  async listProducts(
    page = 1,
    size = 20,
    search?: string,
    hasInfo?: boolean,
  ): Promise<{
    items: ProductInfoListItem[];
    total_count: number;
    page: number;
    size: number;
    total_pages: number;
  }> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    if (search) params.set('search', search);
    if (hasInfo !== undefined) params.set('has_info', String(hasInfo));

    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info?${params}`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed to list products: ${res.status}`);
    const data = await res.json();
    return data;
  }

  /** Get additional info for a product (backoffice). */
  async getProductInfo(productId: string): Promise<{
    product: any;
    additional_info: ProductAdditionalInfo | null;
  }> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error(`Failed to get product info: ${res.status}`);
    const data = await res.json();
    return {
      product: data.product,
      additional_info: resolveImageUrls(data.additional_info),
    };
  }

  /** Create or update additional info (backoffice). */
  async saveProductInfo(
    productId: string,
    data: {
      description_id?: string;
      description_en?: string;
      finish_type?: string;
    },
  ): Promise<ProductAdditionalInfo> {
    const formData = new FormData();
    if (data.description_id !== undefined) formData.append('description_id', data.description_id);
    if (data.description_en !== undefined) formData.append('description_en', data.description_en);
    if (data.finish_type !== undefined) formData.append('finish_type', data.finish_type);

    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}`,
      {
        method: 'POST',
        headers: getBackofficeHeaders(),
        body: formData,
      },
    );
    if (!res.ok) throw new Error(`Failed to save product info: ${res.status}`);
    const result = await res.json();
    return resolveImageUrls(result.additional_info)!;
  }

  /** Delete additional info and all images (backoffice). */
  async deleteProductInfo(productId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}`,
      {
        method: 'DELETE',
        headers: getBackofficeHeaders(),
      },
    );
    if (!res.ok) throw new Error(`Failed to delete product info: ${res.status}`);
  }

  /** Upload an image (backoffice). */
  async uploadImage(
    productId: string,
    file: File,
    imageType: 'main' | 'color' | 'additional',
    options?: {
      alt_text?: string;
      color_name?: string;
      color_hex?: string;
    },
  ): Promise<ProductImage> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    if (options?.alt_text) formData.append('alt_text', options.alt_text);
    if (options?.color_name) formData.append('color_name', options.color_name);
    if (options?.color_hex) formData.append('color_hex', options.color_hex);

    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}/images`,
      {
        method: 'POST',
        headers: getBackofficeHeaders(),
        body: formData,
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Upload failed: ${res.status}`);
    }
    const result = await res.json();
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    const img = result.image as ProductImage;
    if (!img.file_url.startsWith('http')) {
      img.file_url = `${baseUrl}${img.file_url}`;
    }
    return img;
  }

  /** Delete an image (backoffice). */
  async deleteImage(productId: string, imageId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}/images/${imageId}`,
      {
        method: 'DELETE',
        headers: getBackofficeHeaders(),
      },
    );
    if (!res.ok) throw new Error(`Failed to delete image: ${res.status}`);
  }

  /** Update image metadata (backoffice). */
  async updateImageMeta(
    productId: string,
    imageId: string,
    data: { alt_text?: string; color_name?: string; color_hex?: string },
  ): Promise<ProductImage> {
    const formData = new FormData();
    if (data.alt_text !== undefined) formData.append('alt_text', data.alt_text);
    if (data.color_name !== undefined) formData.append('color_name', data.color_name);
    if (data.color_hex !== undefined) formData.append('color_hex', data.color_hex);

    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/${productId}/images/${imageId}`,
      {
        method: 'PUT',
        headers: getBackofficeHeaders(),
        body: formData,
      },
    );
    if (!res.ok) throw new Error(`Failed to update image: ${res.status}`);
    const result = await res.json();
    return result.image;
  }

  // -------------------------------------------------------------------------
  // CSV Import/Export (backoffice)
  // -------------------------------------------------------------------------

  /** Download the CSV template file. */
  async downloadTemplate(): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/csv/template`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error('Failed to download template');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_info_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Export existing product info as CSV. */
  async exportData(): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/csv/export`,
      { headers: getBackofficeHeaders() },
    );
    if (!res.ok) throw new Error('Failed to export data');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_info_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Validate a CSV file (dry run, no changes). */
  async validateCsv(file: File): Promise<CsvValidationResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/csv/validate`,
      { method: 'POST', headers: getBackofficeHeaders(), body: formData },
    );
    if (!res.ok) throw new Error('Validation failed');
    return res.json();
  }

  /** Import a CSV file (execute). */
  async importCsv(file: File): Promise<CsvImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      `${API_BASE_URL}/backoffice/product-info/csv/import`,
      { method: 'POST', headers: getBackofficeHeaders(), body: formData },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Import failed: ${res.status}`);
    }
    return res.json();
  }

  // -------------------------------------------------------------------------
  // Customer-facing endpoints
  // -------------------------------------------------------------------------

  /** Get full product detail with additional info + pricing (customer). */
  async getProductDetail(productId: string): Promise<ProductDetail> {
    const res = await apiClient.get<ProductDetail>(
      `/products/${productId}/detail`,
      getAuthHeaders(),
    );
    if (res.additional_info) {
      res.additional_info = resolveImageUrls(res.additional_info)!;
    }
    return res;
  }
}

export const productInfoService = new ProductInfoService();
